/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  AfterContentInit,
  AfterContentChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  Optional,
  Output,
  QueryList,
  Renderer2,
  Self,
  ViewChild,
  ViewEncapsulation,
  Inject
} from '@angular/core';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {coerceBooleanProperty, Platform} from '../core';
import {FormGroupDirective, NgControl, NgForm} from '@angular/forms';
import {getSupportedInputTypes} from '../core/platform/features';
import {
  getMdInputContainerDuplicatedHintError,
  getMdInputContainerMissingMdInputError,
  getMdInputContainerPlaceholderConflictError,
  getMdInputContainerUnsupportedTypeError
} from './input-container-errors';
import {
  FloatPlaceholderType,
  PlaceholderOptions,
  MD_PLACEHOLDER_GLOBAL_OPTIONS
} from '../core/placeholder/placeholder-options';

// Invalid input type. Using one of these will throw an MdInputContainerUnsupportedTypeError.
const /** @type {?} */ MD_INPUT_INVALID_TYPES = [
  'button',
  'checkbox',
  'color',
  'file',
  'hidden',
  'image',
  'radio',
  'range',
  'reset',
  'submit'
];

let /** @type {?} */ nextUniqueId = 0;
/**
 * The placeholder directive. The content can declare this to implement more
 * complex placeholders.
 */
export class MdPlaceholder {static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: 'md-placeholder, mat-placeholder'
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function MdPlaceholder_tsickle_Closure_declarations() {
/** @type {?} */
MdPlaceholder.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdPlaceholder.ctorParameters;
}

/**
 * Hint text to be shown underneath the input.
 */
export class MdHint {
/**
 * Whether to align the hint label at the start or end of the line.
 */
align: 'start' | 'end' = 'start';
/**
 * Unique ID for the hint. Used for the aria-describedby on the input.
 */
id: string = `md-input-hint-${nextUniqueId++}`;
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: 'md-hint, mat-hint',
  host: {
    'class': 'mat-hint',
    '[class.mat-right]': 'align == "end"',
    '[attr.id]': 'id',
  }
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'align': [{ type: Input },],
'id': [{ type: Input },],
};
}

function MdHint_tsickle_Closure_declarations() {
/** @type {?} */
MdHint.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdHint.ctorParameters;
/** @type {?} */
MdHint.propDecorators;
/**
 * Whether to align the hint label at the start or end of the line.
 * @type {?}
 */
MdHint.prototype.align;
/**
 * Unique ID for the hint. Used for the aria-describedby on the input.
 * @type {?}
 */
MdHint.prototype.id;
}

/**
 * Single error message to be shown underneath the input.
 */
export class MdErrorDirective { static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: 'md-error, mat-error',
  host: {
    'class': 'mat-input-error'
  }
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function MdErrorDirective_tsickle_Closure_declarations() {
/** @type {?} */
MdErrorDirective.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdErrorDirective.ctorParameters;
}

/**
 * Prefix to be placed the the front of the input.
 */
export class MdPrefix {static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: '[mdPrefix], [matPrefix], [md-prefix]'
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function MdPrefix_tsickle_Closure_declarations() {
/** @type {?} */
MdPrefix.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdPrefix.ctorParameters;
}

/**
 * Suffix to be placed at the end of the input.
 */
export class MdSuffix {static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: '[mdSuffix], [matSuffix], [md-suffix]'
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function MdSuffix_tsickle_Closure_declarations() {
/** @type {?} */
MdSuffix.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdSuffix.ctorParameters;
}

/**
 * Marker for the input element that `MdInputContainer` is wrapping.
 */
export class MdInputDirective {
/**
 * Variables used as cache for getters and setters.
 */
private _type = 'text';
private _placeholder: string = '';
private _disabled = false;
private _required = false;
private _id: string;
private _cachedUid: string;
/**
 * Whether the element is focused or not.
 */
focused = false;
/**
 * Sets the aria-describedby attribute on the input for improved a11y.
 */
ariaDescribedby: string;
/**
 * Whether the element is disabled.
 * @return {?}
 */
get disabled() {
    return this._ngControl ? this._ngControl.disabled : this._disabled;
  }
/**
 * @param {?} value
 * @return {?}
 */
set disabled(value: any) {
    this._disabled = coerceBooleanProperty(value);
  }
/**
 * Unique id of the element.
 * @return {?}
 */
get id() { return this._id; }
/**
 * @param {?} value
 * @return {?}
 */
set id(value: string) {this._id = value || this._uid; }
/**
 * Placeholder attribute of the element.
 * @return {?}
 */
get placeholder() { return this._placeholder; }
/**
 * @param {?} value
 * @return {?}
 */
set placeholder(value: string) {
    if (this._placeholder !== value) {
      this._placeholder = value;
      this._placeholderChange.emit(this._placeholder);
    }
  }
/**
 * Whether the element is required.
 * @return {?}
 */
get required() { return this._required; }
/**
 * @param {?} value
 * @return {?}
 */
set required(value: any) { this._required = coerceBooleanProperty(value); }
/**
 * Input type of the element.
 * @return {?}
 */
get type() { return this._type; }
/**
 * @param {?} value
 * @return {?}
 */
set type(value: string) {
    this._type = value || 'text';
    this._validateType();

    // When using Angular inputs, developers are no longer able to set the properties on the native
    // input element. To ensure that bindings for `type` work, we need to sync the setter
    // with the native property. Textarea elements don't support the type property or attribute.
    if (!this._isTextarea() && getSupportedInputTypes().has(this._type)) {
      this._renderer.setProperty(this._elementRef.nativeElement, 'type', this._type);
    }
  }
/**
 * The input element's value.
 * @return {?}
 */
get value() { return this._elementRef.nativeElement.value; }
/**
 * @param {?} value
 * @return {?}
 */
set value(value: string) { this._elementRef.nativeElement.value = value; }
/**
 * Emits an event when the placeholder changes so that the `md-input-container` can re-validate.
 */
_placeholderChange = new EventEmitter<string>();
/**
 * Whether the input is empty.
 * @return {?}
 */
get empty() {
    return !this._isNeverEmpty() &&
        (this.value == null || this.value === '') &&
        // Check if the input contains bad input. If so, we know that it only appears empty because
        // the value failed to parse. From the user's perspective it is not empty.
        // TODO(mmalerba): Add e2e test for bad input case.
        !this._isBadInput();
  }
/**
 * @return {?}
 */
private get _uid() { return this._cachedUid = this._cachedUid || `md-input-${nextUniqueId++}`; }
private _neverEmptyInputTypes = [
    'date',
    'datetime',
    'datetime-local',
    'month',
    'time',
    'week'
  ].filter(t => getSupportedInputTypes().has(t));
/**
 * @param {?} _elementRef
 * @param {?} _renderer
 * @param {?} _platform
 * @param {?} _ngControl
 * @param {?} _parentForm
 * @param {?} _parentFormGroup
 */
constructor(private _elementRef: ElementRef,
private _renderer: Renderer2,
private _platform: Platform,
public _ngControl: NgControl,
private _parentForm: NgForm,
private _parentFormGroup: FormGroupDirective) {

    // Force setter to be called in case id was not specified.
    this.id = this.id;
  }
/**
 * Focuses the input element.
 * @return {?}
 */
focus() { this._elementRef.nativeElement.focus(); }
/**
 * @return {?}
 */
_onFocus() { this.focused = true; }
/**
 * @return {?}
 */
_onBlur() { this.focused = false; }
/**
 * @return {?}
 */
_onInput() {
    // This is a noop function and is used to let Angular know whenever the value changes.
    // Angular will run a new change detection each time the `input` event has been dispatched.
    // It's necessary that Angular recognizes the value change, because when floatingLabel
    // is set to false and Angular forms aren't used, the placeholder won't recognize the
    // value changes and will not disappear.
    // Listening to the input event wouldn't be necessary when the input is using the
    // FormsModule or ReactiveFormsModule, because Angular forms also listens to input events.
  }
/**
 * Whether the input is in an error state.
 * @return {?}
 */
_isErrorState(): boolean {
    const /** @type {?} */ control = this._ngControl;
    const /** @type {?} */ isInvalid = control && control.invalid;
    const /** @type {?} */ isTouched = control && control.touched;
    const /** @type {?} */ isSubmitted = (this._parentFormGroup && this._parentFormGroup.submitted) ||
        (this._parentForm && this._parentForm.submitted);

    return !!(isInvalid && (isTouched || isSubmitted));
  }
/**
 * Make sure the input is a supported type.
 * @return {?}
 */
private _validateType() {
    if (MD_INPUT_INVALID_TYPES.indexOf(this._type) !== -1) {
      throw getMdInputContainerUnsupportedTypeError(this._type);
    }
  }
/**
 * @return {?}
 */
private _isNeverEmpty() { return this._neverEmptyInputTypes.indexOf(this._type) !== -1; }
/**
 * @return {?}
 */
private _isBadInput() {
    // The `validity` property won't be present on platform-server.
    let /** @type {?} */ validity = ( /** @type {?} */((this._elementRef.nativeElement as HTMLInputElement))).validity;
    return validity && validity.badInput;
  }
/**
 * Determines if the component host is a textarea. If not recognizable it returns false.
 * @return {?}
 */
private _isTextarea() {
    let /** @type {?} */ nativeElement = this._elementRef.nativeElement;

    // In Universal, we don't have access to `nodeName`, but the same can be achieved with `name`.
    // Note that this shouldn't be necessary once Angular switches to an API that resembles the
    // DOM closer.
    let /** @type {?} */ nodeName = this._platform.isBrowser ? nativeElement.nodeName : nativeElement.name;
    return nodeName ? nodeName.toLowerCase() === 'textarea' : false;
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: `input[mdInput], textarea[mdInput], input[matInput], textarea[matInput]`,
  host: {
    'class': 'mat-input-element',
    // Native input properties that are overwritten by Angular inputs need to be synced with
    // the native input element. Otherwise property bindings for those don't work.
    '[id]': 'id',
    '[placeholder]': 'placeholder',
    '[disabled]': 'disabled',
    '[required]': 'required',
    '[attr.aria-describedby]': 'ariaDescribedby || null',
    '[attr.aria-invalid]': '_isErrorState()',
    '(blur)': '_onBlur()',
    '(focus)': '_onFocus()',
    '(input)': '_onInput()',
  }
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: Renderer2, },
{type: Platform, },
{type: NgControl, decorators: [{ type: Optional }, { type: Self }, ]},
{type: NgForm, decorators: [{ type: Optional }, ]},
{type: FormGroupDirective, decorators: [{ type: Optional }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'disabled': [{ type: Input },],
'id': [{ type: Input },],
'placeholder': [{ type: Input },],
'required': [{ type: Input },],
'type': [{ type: Input },],
'_placeholderChange': [{ type: Output },],
};
}

function MdInputDirective_tsickle_Closure_declarations() {
/** @type {?} */
MdInputDirective.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdInputDirective.ctorParameters;
/** @type {?} */
MdInputDirective.propDecorators;
/**
 * Variables used as cache for getters and setters.
 * @type {?}
 */
MdInputDirective.prototype._type;
/** @type {?} */
MdInputDirective.prototype._placeholder;
/** @type {?} */
MdInputDirective.prototype._disabled;
/** @type {?} */
MdInputDirective.prototype._required;
/** @type {?} */
MdInputDirective.prototype._id;
/** @type {?} */
MdInputDirective.prototype._cachedUid;
/**
 * Whether the element is focused or not.
 * @type {?}
 */
MdInputDirective.prototype.focused;
/**
 * Sets the aria-describedby attribute on the input for improved a11y.
 * @type {?}
 */
MdInputDirective.prototype.ariaDescribedby;
/**
 * Emits an event when the placeholder changes so that the `md-input-container` can re-validate.
 * @type {?}
 */
MdInputDirective.prototype._placeholderChange;
/** @type {?} */
MdInputDirective.prototype._neverEmptyInputTypes;
/** @type {?} */
MdInputDirective.prototype._elementRef;
/** @type {?} */
MdInputDirective.prototype._renderer;
/** @type {?} */
MdInputDirective.prototype._platform;
/** @type {?} */
MdInputDirective.prototype._ngControl;
/** @type {?} */
MdInputDirective.prototype._parentForm;
/** @type {?} */
MdInputDirective.prototype._parentFormGroup;
}

/**
 * Container for text inputs that applies Material Design styling and behavior.
 */
export class MdInputContainer implements AfterViewInit, AfterContentInit, AfterContentChecked {
private _placeholderOptions: PlaceholderOptions;
/**
 * Color of the input divider, based on the theme.
 */
color: 'primary' | 'accent' | 'warn' = 'primary';
/**
 * @deprecated Use color instead.
 * @return {?}
 */
get dividerColor() { return this.color; }
/**
 * @param {?} value
 * @return {?}
 */
set dividerColor(value) { this.color = value; }
/**
 * Whether the required marker should be hidden.
 * @return {?}
 */
get hideRequiredMarker() { return this._hideRequiredMarker; }
/**
 * @param {?} value
 * @return {?}
 */
set hideRequiredMarker(value: any) {
    this._hideRequiredMarker = coerceBooleanProperty(value);
  }
private _hideRequiredMarker: boolean;
/**
 * Whether the floating label should always float or not.
 * @return {?}
 */
get _shouldAlwaysFloat() { return this._floatPlaceholder === 'always'; }
/**
 * Whether the placeholder can float or not.
 * @return {?}
 */
get _canPlaceholderFloat() { return this._floatPlaceholder !== 'never'; }
/**
 * State of the md-hint and md-error animations.
 */
_subscriptAnimationState: string = '';
/**
 * Text for the input hint.
 * @return {?}
 */
get hintLabel() { return this._hintLabel; }
/**
 * @param {?} value
 * @return {?}
 */
set hintLabel(value: string) {
    this._hintLabel = value;
    this._processHints();
  }
private _hintLabel = '';

  // Unique id for the hint label.
  _hintLabelId: string = `md-input-hint-${nextUniqueId++}`;
/**
 * Whether the placeholder should always float, never float or float as the user types.
 * @return {?}
 */
get floatPlaceholder() { return this._floatPlaceholder; }
/**
 * @param {?} value
 * @return {?}
 */
set floatPlaceholder(value: FloatPlaceholderType) {
    this._floatPlaceholder = value || this._placeholderOptions.float || 'auto';
  }
private _floatPlaceholder: FloatPlaceholderType;
/**
 * Reference to the input's underline element.
 */
underlineRef: ElementRef;

   _mdInputChild: MdInputDirective;

   _placeholderChild: MdPlaceholder;

   _errorChildren: QueryList<MdErrorDirective>;

   _hintChildren: QueryList<MdHint>;

   _prefixChildren: QueryList<MdPrefix>;

   _suffixChildren: QueryList<MdSuffix>;
/**
 * @param {?} _elementRef
 * @param {?} _changeDetectorRef
 * @param {?} placeholderOptions
 */
constructor(
public _elementRef: ElementRef,
private _changeDetectorRef: ChangeDetectorRef,
      placeholderOptions: PlaceholderOptions) {
      this._placeholderOptions = placeholderOptions ? placeholderOptions : {};
      this.floatPlaceholder = this._placeholderOptions.float || 'auto';
    }
/**
 * @return {?}
 */
ngAfterContentInit() {
    this._validateInputChild();
    this._processHints();
    this._validatePlaceholders();

    // Re-validate when things change.
    this._hintChildren.changes.subscribe(() => this._processHints());
    this._mdInputChild._placeholderChange.subscribe(() => this._validatePlaceholders());
  }
/**
 * @return {?}
 */
ngAfterContentChecked() {
    this._validateInputChild();
  }
/**
 * @return {?}
 */
ngAfterViewInit() {
    // Avoid animations on load.
    this._subscriptAnimationState = 'enter';
    this._changeDetectorRef.detectChanges();
  }
/**
 * Determines whether a class from the NgControl should be forwarded to the host element.
 * @param {?} prop
 * @return {?}
 */
_shouldForward(prop: string): boolean {
    let /** @type {?} */ control = this._mdInputChild ? this._mdInputChild._ngControl : null;
    return control && ( /** @type {?} */((control as any)))[prop];
  }
/**
 * Whether the input has a placeholder.
 * @return {?}
 */
_hasPlaceholder() { return !!(this._mdInputChild.placeholder || this._placeholderChild); }
/**
 * Focuses the underlying input.
 * @return {?}
 */
_focusInput() { this._mdInputChild.focus(); }
/**
 * Determines whether to display hints or errors.
 * @return {?}
 */
_getDisplayedMessages(): 'error' | 'hint' {
    let /** @type {?} */ input = this._mdInputChild;
    return (this._errorChildren.length > 0 && input._isErrorState()) ? 'error' : 'hint';
  }
/**
 * Ensure that there is only one placeholder (either `input` attribute or child element with the
 * `md-placeholder` attribute.
 * @return {?}
 */
private _validatePlaceholders() {
    if (this._mdInputChild.placeholder && this._placeholderChild) {
      throw getMdInputContainerPlaceholderConflictError();
    }
  }
/**
 * Does any extra processing that is required when handling the hints.
 * @return {?}
 */
private _processHints() {
    this._validateHints();
    this._syncAriaDescribedby();
  }
/**
 * Ensure that there is a maximum of one of each `<md-hint>` alignment specified, with the
 * attribute being considered as `align="start"`.
 * @return {?}
 */
private _validateHints() {
    if (this._hintChildren) {
      let /** @type {?} */ startHint: MdHint = null;
      let /** @type {?} */ endHint: MdHint = null;
      this._hintChildren.forEach((hint: MdHint) => {
        if (hint.align == 'start') {
          if (startHint || this.hintLabel) {
            throw getMdInputContainerDuplicatedHintError('start');
          }
          startHint = hint;
        } else if (hint.align == 'end') {
          if (endHint) {
            throw getMdInputContainerDuplicatedHintError('end');
          }
          endHint = hint;
        }
      });
    }
  }
/**
 * Sets the child input's `aria-describedby` to a space-separated list of the ids
 * of the currently-specified hints, as well as a generated id for the hint label.
 * @return {?}
 */
private _syncAriaDescribedby() {
    if (this._mdInputChild) {
      let /** @type {?} */ ids: string[] = [];
      let /** @type {?} */ startHint = this._hintChildren ?
          this._hintChildren.find(hint => hint.align === 'start') : null;
      let /** @type {?} */ endHint = this._hintChildren ?
          this._hintChildren.find(hint => hint.align === 'end') : null;

      if (startHint) {
        ids.push(startHint.id);
      } else if (this._hintLabel) {
        ids.push(this._hintLabelId);
      }

      if (endHint) {
        ids.push(endHint.id);
      }

      this._mdInputChild.ariaDescribedby = ids.join(' ');
    }
  }
/**
 * Throws an error if the container's input child was removed.
 * @return {?}
 */
private _validateInputChild() {
    if (!this._mdInputChild) {
      throw getMdInputContainerMissingMdInputError();
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-input-container, mat-input-container',
  templateUrl: 'input-container.html',
  styleUrls: ['input-container.css'],
  animations: [
    trigger('transitionMessages', [
      state('enter', style({ opacity: 1, transform: 'translateY(0%)' })),
      transition('void => enter', [
        style({ opacity: 0, transform: 'translateY(-100%)' }),
        animate('300ms cubic-bezier(0.55, 0, 0.55, 0.2)')
      ])
    ])
  ],
  host: {
    // Remove align attribute to prevent it from interfering with layout.
    '[attr.align]': 'null',
    'class': 'mat-input-container',
    '[class.mat-input-invalid]': '_mdInputChild._isErrorState()',
    '[class.mat-focused]': '_mdInputChild.focused',
    '[class.ng-untouched]': '_shouldForward("untouched")',
    '[class.ng-touched]': '_shouldForward("touched")',
    '[class.ng-pristine]': '_shouldForward("pristine")',
    '[class.ng-dirty]': '_shouldForward("dirty")',
    '[class.ng-valid]': '_shouldForward("valid")',
    '[class.ng-invalid]': '_shouldForward("invalid")',
    '[class.ng-pending]': '_shouldForward("pending")',
    '(click)': '_focusInput()',
  },
  encapsulation: ViewEncapsulation.None,
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: ChangeDetectorRef, },
{type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [MD_PLACEHOLDER_GLOBAL_OPTIONS, ] }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'color': [{ type: Input },],
'dividerColor': [{ type: Input },],
'hideRequiredMarker': [{ type: Input },],
'hintLabel': [{ type: Input },],
'floatPlaceholder': [{ type: Input },],
'underlineRef': [{ type: ViewChild, args: ['underline', ] },],
'_mdInputChild': [{ type: ContentChild, args: [MdInputDirective, ] },],
'_placeholderChild': [{ type: ContentChild, args: [MdPlaceholder, ] },],
'_errorChildren': [{ type: ContentChildren, args: [MdErrorDirective, ] },],
'_hintChildren': [{ type: ContentChildren, args: [MdHint, ] },],
'_prefixChildren': [{ type: ContentChildren, args: [MdPrefix, ] },],
'_suffixChildren': [{ type: ContentChildren, args: [MdSuffix, ] },],
};
}

function MdInputContainer_tsickle_Closure_declarations() {
/** @type {?} */
MdInputContainer.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdInputContainer.ctorParameters;
/** @type {?} */
MdInputContainer.propDecorators;
/** @type {?} */
MdInputContainer.prototype._placeholderOptions;
/**
 * Color of the input divider, based on the theme.
 * @type {?}
 */
MdInputContainer.prototype.color;
/** @type {?} */
MdInputContainer.prototype._hideRequiredMarker;
/**
 * State of the md-hint and md-error animations.
 * @type {?}
 */
MdInputContainer.prototype._subscriptAnimationState;
/** @type {?} */
MdInputContainer.prototype._hintLabel;
/** @type {?} */
MdInputContainer.prototype._hintLabelId;
/** @type {?} */
MdInputContainer.prototype._floatPlaceholder;
/**
 * Reference to the input's underline element.
 * @type {?}
 */
MdInputContainer.prototype.underlineRef;
/** @type {?} */
MdInputContainer.prototype._mdInputChild;
/** @type {?} */
MdInputContainer.prototype._placeholderChild;
/** @type {?} */
MdInputContainer.prototype._errorChildren;
/** @type {?} */
MdInputContainer.prototype._hintChildren;
/** @type {?} */
MdInputContainer.prototype._prefixChildren;
/** @type {?} */
MdInputContainer.prototype._suffixChildren;
/** @type {?} */
MdInputContainer.prototype._elementRef;
/** @type {?} */
MdInputContainer.prototype._changeDetectorRef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
